import { Avatar, Box, Card, Grid } from "@mui/material";
import {
  DataGridPro,
  type DataGridProProps,
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridEventListener,
  type GridPinnedColumns,
  type GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { lowerCase } from "lodash-es";
import { type FC, type PropsWithChildren, useMemo } from "react";
import { useNavigate } from "react-router";
import { propertyNotEmpty } from "../../utils/not-empty";
import { ConditionalWrapper } from "../generic/components/conditional-wrapper";
import { MomentCardActions } from "../moment-card/components/moment-card-actions";
import type { UseMomentProperties } from "../moment-card/types";
import { ProgramIcon } from "../programs/components/program-icon";
import { useColumns } from "./program-list-config";
import type { AvailableColumns, MomentTableProps } from "./types";

interface Props {
  moments: UseMomentProperties[];
  reloadMoments?: () => void;
  expandedPrograms?: string[];
  onProgramExpansion?: (programId: string) => void;
  setProgramExpandedState?: (programIds: string[], isExpanded: boolean) => void;
  columns?: (keyof AvailableColumns)[];
  wrapInCard?: boolean;
}

export const ProgramListView: FC<Props & PropsWithChildren> = ({
  moments,
  reloadMoments,
  children,
  columns = ["status", "when", "segment", "channel"],
  wrapInCard = true,
}) => {
  const apiRef = useGridApiRef();

  const getTreeDataPath: DataGridProProps<MomentTableProps>["getTreeDataPath"] = (row) =>
    row.hierarchy;

  const groupingColDef: DataGridProProps<MomentTableProps>["groupingColDef"] = {
    headerName: "Program",
    minWidth: 390,
    hideDescendantCount: true,
    pinnable: true,
    sortable: false,
    valueGetter: (_, row) => {
      if (row.type === "program") {
        const { theme_colour, theme_icon } = row.data;
        const id = lowerCase(row.title).replaceAll(" ", "-");

        return (
          <Grid container spacing={1} id={id}>
            <Grid>
              <Avatar sx={{ bgcolor: theme_colour, width: "30px", height: "30px" }}>
                <ProgramIcon data={theme_icon} fontSize="small" />
              </Avatar>
            </Grid>
            <Grid sx={{ mt: 0.5 }}>{row.title}</Grid>
          </Grid>
        );
      } else if (row.type === "moment") {
        const { valid_on_save, status } = row.data;
        const publishable = valid_on_save || status === "active" || status === "finished";

        let offset = 10;
        if (!publishable) {
          offset += 58;
        }

        return (
          <Grid container sx={{ width: 1420 }}>
            <Grid sx={{ my: "auto" }}>
              <MomentCardActions reload={reloadMoments} moment={row.data} />
            </Grid>
            <Grid sx={{ my: "auto", ml: `${offset}px` }}>{row.title}</Grid>
          </Grid>
        );
      }
    },
  };

  const pinnedColumns: GridPinnedColumns | undefined = undefined;
  const sortModel: GridSortModel = [{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: "asc" }];

  const rows = useMemo(() => {
    const nonEmptyMoments = moments.filter(propertyNotEmpty("program"));

    const momentRows: MomentTableProps[] = nonEmptyMoments.map((moment) => {
      const { title, status, id, program, segment, channel } = moment;

      return {
        id: (id ?? "") + "_" + (title ?? ""),
        hierarchy: [program.id, id as string],
        title: title ?? "",
        type: "moment",
        status: status ?? "draft",
        segment: segment?.name || "",
        channel: channel,
        data: moment,
      };
    });

    return momentRows;
  }, [moments]);

  const navigate = useNavigate();

  const handleCellClick: GridEventListener<"cellClick"> = (params) => {
    const row = params.row as MomentTableProps;
    switch (params.field) {
      case "actions":
        break;
      default:
        if (row.type === "moment" && row.data.id !== null) {
          void navigate(`/moments/${row.data.id}`);
        }
        break;
    }
  };

  const cols = useColumns({ columns, reload: reloadMoments });
  return (
    <>
      <ConditionalWrapper
        condition={wrapInCard}
        wrapper={(c) => <Card sx={{ width: "100%" }}>{c}</Card>}
      >
        <DataGridPro<MomentTableProps>
          apiRef={apiRef}
          rows={rows}
          columns={cols}
          getTreeDataPath={getTreeDataPath}
          groupingColDef={groupingColDef}
          onCellClick={handleCellClick}
          initialState={{
            pinnedColumns,
            sorting: {
              sortModel,
            },
          }}
          sortingOrder={["asc", "desc"]}
          hideFooterSelectedRowCount
          hideFooter
          sx={{
            "& .MuiDataGrid-row, & .MuiDataGrid-row *": { cursor: "pointer" },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box ml={2} mt={4}>
                {children}
              </Box>
            ),
          }}
        />
      </ConditionalWrapper>
    </>
  );
};
